import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import { Notification, toArray } from 'src/utils';
import { ICreateTaskInput } from 'src/interfaces/task';
import { ICell } from 'src/interfaces/cell';
import { ISector } from 'src/interfaces/sector';
import { IPatrimony } from 'src/interfaces/patrimony';

import { IProps } from './create.type';

const getErrorMessage = (err: any, fallback: string) =>
  err?.message || fallback;

const useTask = (props: IProps) => {
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [patrimonies, setPatrimonies] = useState<
    { value: string; label: string }[] | undefined
  >(undefined);
  const [operators, setOperators] = useState<
    { value: string; label: string }[] | undefined
  >(undefined);
  const [cells, setCells] = useState<ICell[] | undefined>(undefined);
  const [sectors, setSectors] = useState<ISector[] | undefined>(undefined);

  const handleSubmit = async (values: ICreateTaskInput | any) => {
    setSending(true);

    values.stage = Number(searchParams.get('stage')) || 0;

    try {
      const data = await props.createTask(values);

      if (!!data) {
        Notification.success({
          title: 'Sucesso',
          message: 'Tarefa cadastrada com sucesso',
        });

        navigate(-1);
      }
    } finally {
      setSending(false);
    }
  };

  const handlePatrimonies = async () => {
    setLoading(true);

    try {
      const response = await props.fetchPatrimonies();
      const list = toArray<IPatrimony>(response, 'Patrimônios');

      if (!list.length) {
        Notification.warning({
          title: 'Dados incompletos',
          message:
            'A listagem de patrimônios veio vazia. Verifique se existem patrimônios cadastrados.',
        });
      }

      setPatrimonies(
        list.map((item) => ({
          value: String(item.id),
          label: `${item.number || '-'} | ${item.description} (M: ${
            item.model
          }) (SN: ${item.serialNumber})`,
        }))
      );
    } catch (err: any) {
      Notification.error({
        message: getErrorMessage(
          err,
          'Ocorreu um erro ao carregar a listagem de patrimônios, tente novamente mais tarde.'
        ),
      });

      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleCells = async () => {
    setLoading(true);

    try {
      const response = await props.fetchCells();
      const list = toArray<ICell>(response, 'Células');

      if (!list.length) {
        Notification.warning({
          title: 'Dados incompletos',
          message:
            'A listagem de células veio vazia. Verifique se existem células cadastradas.',
        });
      }

      setCells(list);
    } catch (err: any) {
      Notification.error({
        message: getErrorMessage(
          err,
          'Ocorreu um erro ao carregar a listagem de células, tente novamente mais tarde.'
        ),
      });

      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleOperators = async () => {
    setLoading(true);

    try {
      // fetchOperators already returns { value, label }[]
      const response = await props.fetchOperators();
      const list = toArray<{ value: string; label: string }>(
        response,
        'Operadores'
      );

      if (!list.length) {
        Notification.warning({
          title: 'Dados incompletos',
          message:
            'A listagem de operadores veio vazia. Verifique se existem operadores cadastrados.',
        });
      }

      setOperators(list);
    } catch (err: any) {
      Notification.error({
        message: getErrorMessage(
          err,
          'Ocorreu um erro ao carregar a listagem de operadores, tente novamente mais tarde.'
        ),
      });

      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleSectors = async () => {
    setLoading(true);

    try {
      const response = await props.fetchSectors();
      const list = toArray<ISector>(response, 'Setores');

      if (!list.length) {
        Notification.warning({
          title: 'Dados incompletos',
          message:
            'A listagem de setores veio vazia. Verifique se existem setores cadastrados.',
        });
      }

      setSectors(list);
    } catch (err: any) {
      Notification.error({
        message: getErrorMessage(
          err,
          'Ocorreu um erro ao carregar a listagem de setores, tente novamente mais tarde.'
        ),
      });

      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    handlePatrimonies,
    patrimonies,
    handleCells,
    cells,
    handleOperators,
    operators,
    handleSectors,
    sectors,
    handleSubmit,
    sending,
    loading,
    navigate,
  };
};

export default useTask;
